import React, { useEffect, useState } from "react";
import { useScrollAnimation } from "../../hooks/useScrollAnimation";

const PrivacyPolicy = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [headerRef, headerVisible] = useScrollAnimation({ threshold: 0.2 });

  useEffect(() => {
    setIsVisible(true);
    window.scrollTo(0, 0);
  }, []);

  const sections = [
    {
      title: "1. Information We Collect",
      content: [
        {
          subtitle: "Personal Information",
          text: "When you create an account or use our services, we may collect personal information such as your name, email address, phone number, and billing information."
        },
        {
          subtitle: "Vehicle Information",
          text: "We collect vehicle identification numbers (VINs), vehicle specifications, mileage, condition reports, and other vehicle-related data necessary to provide accurate appraisals."
        },
        {
          subtitle: "Usage Data",
          text: "We automatically collect information about how you interact with our platform, including IP addresses, browser type, pages visited, and time spent on our services."
        }
      ]
    },
    {
      title: "2. How We Use Your Information",
      content: [
        {
          subtitle: "Service Delivery",
          text: "We use your information to provide vehicle appraisal services, process transactions, and deliver accurate valuations based on market data."
        },
        {
          subtitle: "Communication",
          text: "We may use your contact information to send service updates, respond to inquiries, and provide customer support."
        },
        {
          subtitle: "Improvement",
          text: "We analyze usage patterns to improve our platform, develop new features, and enhance user experience."
        }
      ]
    },
    {
      title: "3. Information Sharing",
      content: [
        {
          subtitle: "Third-Party Services",
          text: "We may share information with trusted third-party service providers who assist in operating our platform, such as payment processors and cloud hosting services."
        },
        {
          subtitle: "Legal Requirements",
          text: "We may disclose information when required by law, court order, or to protect our rights, safety, or property."
        },
        {
          subtitle: "Business Transfers",
          text: "In the event of a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction."
        }
      ]
    },
    {
      title: "4. Data Security",
      content: [
        {
          subtitle: "Protection Measures",
          text: "We implement industry-standard security measures including encryption, secure servers, and regular security audits to protect your personal information."
        },
        {
          subtitle: "Access Controls",
          text: "Access to personal data is restricted to authorized personnel who need it to perform their job functions."
        }
      ]
    },
    {
      title: "5. Your Rights",
      content: [
        {
          subtitle: "Access and Correction",
          text: "You have the right to access, update, or correct your personal information at any time through your account settings."
        },
        {
          subtitle: "Data Deletion",
          text: "You may request deletion of your account and associated data by contacting our support team."
        },
        {
          subtitle: "Opt-Out",
          text: "You can opt out of marketing communications at any time by clicking the unsubscribe link in our emails or updating your preferences."
        }
      ]
    },
    {
      title: "6. Cookies and Tracking",
      content: [
        {
          subtitle: "Cookie Usage",
          text: "We use cookies and similar technologies to enhance your experience, remember preferences, and analyze site traffic."
        },
        {
          subtitle: "Third-Party Analytics",
          text: "We may use third-party analytics services to help understand how our platform is used. These services may collect information about your use of our website."
        }
      ]
    },
    {
      title: "7. Children's Privacy",
      content: [
        {
          subtitle: "Age Restriction",
          text: "Our services are not intended for individuals under 18 years of age. We do not knowingly collect personal information from children."
        }
      ]
    },
    {
      title: "8. Changes to This Policy",
      content: [
        {
          subtitle: "Updates",
          text: "We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new policy on this page and updating the effective date."
        }
      ]
    },
    {
      title: "9. Contact Us",
      content: [
        {
          subtitle: "Questions",
          text: "If you have any questions about this Privacy Policy, please contact us at support@appraiserassistant.com or through our Contact Us page."
        }
      ]
    }
  ];

  return (
    <div className="relative min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 overflow-hidden">
      {/* Animated Background Blobs */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-0 -left-4 w-72 h-72 bg-blue-500 rounded-full mix-blend-multiply filter blur-xl animate-blob"></div>
        <div className="absolute top-0 -right-4 w-72 h-72 bg-cyan-500 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-2000"></div>
        <div className="absolute -bottom-8 left-20 w-72 h-72 bg-indigo-500 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-4000"></div>
      </div>

      {/* Grid Pattern Overlay */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff08_1px,transparent_1px),linear-gradient(to_bottom,#ffffff08_1px,transparent_1px)] bg-[size:4rem_4rem]"></div>

      <div className="relative pt-32 pb-20 px-4">
        <div className="container mx-auto max-w-4xl">
          {/* Header */}
          <div
            ref={headerRef}
            className={`text-center mb-12 transition-all duration-700 ${
              headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
          >
            {/* Badge */}
            <div
              className={`inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2 mb-6 transition-all duration-700 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'
              }`}
            >
              <svg className="w-4 h-4 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              <span className="text-sm text-gray-300">Your privacy matters</span>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Privacy{" "}
              <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
                Policy
              </span>
            </h1>
            <p className="text-lg text-gray-300 max-w-2xl mx-auto">
              We are committed to protecting your privacy. This policy explains how we collect, use, and safeguard your information.
            </p>
            <p className="text-sm text-gray-400 mt-4">
              Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </p>
          </div>

          {/* Content */}
          <div
            className={`transition-all duration-700 delay-200 ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
          >
            <div className="bg-white/10 backdrop-blur-xl rounded-3xl border border-white/20 p-8 md:p-12 shadow-2xl">
              {sections.map((section, sectionIndex) => (
                <div key={sectionIndex} className={sectionIndex > 0 ? "mt-10" : ""}>
                  <h2 className="text-xl md:text-2xl font-bold text-white mb-6">
                    {section.title}
                  </h2>
                  <div className="space-y-6">
                    {section.content.map((item, itemIndex) => (
                      <div key={itemIndex}>
                        <h3 className="text-cyan-400 font-semibold mb-2">{item.subtitle}</h3>
                        <p className="text-gray-300 leading-relaxed">{item.text}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
